import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  X, Heart, ExternalLink, Calendar, Tag, Building2, 
  Folder, Sparkles, Check, Copy, Trash2, Plus
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_STAGES = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'consideration', label: 'Consideration' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'retention', label: 'Retention' },
];

const AD_FORMATS = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reel' },
];

const AdDetailPanel = ({ ad, open, onClose }) => {
  const { clients, tags, collections, updateAd, acceptAiTags, deleteAd } = useData();
  const [editedAd, setEditedAd] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (ad) {
      setEditedAd({ ...ad });
    }
  }, [ad]);

  if (!ad || !editedAd) return null;

  const thumbnailUrl = ad.thumbnail_url 
    ? (ad.thumbnail_url.startsWith('/api') ? `${API_URL}${ad.thumbnail_url}` : ad.thumbnail_url)
    : null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAd(ad.id, {
        title: editedAd.title,
        notes: editedAd.notes,
        client_id: editedAd.client_id,
        collection_id: editedAd.collection_id,
        funnel_stage: editedAd.funnel_stage,
        ad_format: editedAd.ad_format,
        hook: editedAd.hook,
        cta: editedAd.cta,
        offer: editedAd.offer,
        competitor: editedAd.competitor,
        is_favorite: editedAd.is_favorite,
        tag_ids: editedAd.tag_ids || [],
      });
      toast.success('Changes saved');
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptAiTags = async () => {
    if (!ad.ai_tags || ad.ai_tags.length === 0) return;
    try {
      await acceptAiTags(ad.id, ad.ai_tags);
      toast.success('AI tags accepted');
    } catch (err) {
      toast.error('Failed to accept tags');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this ad?')) {
      try {
        await deleteAd(ad.id);
        toast.success('Ad deleted');
        onClose();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-lg bg-zinc-900 border-zinc-800 overflow-y-auto"
        data-testid="ad-detail-panel"
      >
        <SheetHeader className="mb-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-zinc-100">Ad Details</SheetTitle>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-zinc-400 hover:text-red-400"
                onClick={handleDelete}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Preview Image */}
        {thumbnailUrl && (
          <div className="relative rounded-lg overflow-hidden mb-6">
            <img
              src={thumbnailUrl}
              alt={ad.title || 'Ad preview'}
              className="w-full h-auto"
            />
            {ad.source_url && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 bg-black/60 text-white hover:bg-black/80"
                onClick={() => window.open(ad.source_url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Original
              </Button>
            )}
          </div>
        )}

        {/* Processing Status */}
        {ad.processing_status === 'processing' && (
          <div className="mb-4 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <div className="flex items-center gap-2 text-violet-400 text-sm">
              <div className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
              AI is analyzing this ad...
            </div>
          </div>
        )}

        {/* AI Suggestions */}
        {ad.ai_tags && ad.ai_tags.length > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm text-violet-400">
                <Sparkles className="w-4 h-4" />
                AI Suggestions
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-violet-400 hover:text-violet-300"
                onClick={handleAcceptAiTags}
              >
                <Check className="w-4 h-4 mr-1" />
                Accept All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {ad.ai_tags.map((tag, i) => (
                <Badge 
                  key={i} 
                  variant="secondary"
                  className="bg-violet-500/20 text-violet-300 border border-violet-500/30"
                >
                  {tag}
                </Badge>
              ))}
            </div>
            {ad.ai_description && (
              <p className="mt-3 text-sm text-zinc-400">{ad.ai_description}</p>
            )}
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-5">
          {/* Title */}
          <div>
            <Label className="text-zinc-400 text-sm">Title</Label>
            <Input
              data-testid="ad-title-input"
              value={editedAd.title || ''}
              onChange={(e) => setEditedAd({ ...editedAd, title: e.target.value })}
              placeholder="Ad title..."
              className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200"
            />
          </div>

          {/* Client */}
          <div>
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Client
            </Label>
            <Select
              value={editedAd.client_id || '_none_'}
              onValueChange={(value) => setEditedAd({ ...editedAd, client_id: value === '_none_' ? null : value })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="client-select">
                <SelectValue placeholder="Select client..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="_none_" className="text-zinc-400">None</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="text-zinc-200">
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Collection */}
          <div>
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Folder className="w-4 h-4" /> Collection
            </Label>
            <Select
              value={editedAd.collection_id || '_none_'}
              onValueChange={(value) => setEditedAd({ ...editedAd, collection_id: value === '_none_' ? null : value })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="collection-select">
                <SelectValue placeholder="Select collection..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="_none_" className="text-zinc-400">None</SelectItem>
                {collections.map((coll) => (
                  <SelectItem key={coll.id} value={coll.id} className="text-zinc-200">
                    {coll.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags */}
          <div>
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Tag className="w-4 h-4" /> Tags
            </Label>
            <div className="mt-2">
              {/* Selected tags display */}
              <div className="flex flex-wrap gap-2 mb-2">
                {(editedAd.tag_ids || []).map((tagId) => {
                  const tag = tags.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <Badge
                      key={tagId}
                      className="bg-violet-500/20 text-violet-300 border border-violet-500/30 cursor-pointer hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30 transition-colors"
                      onClick={() => {
                        setEditedAd({
                          ...editedAd,
                          tag_ids: editedAd.tag_ids.filter(id => id !== tagId)
                        });
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-1.5"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                      <X className="w-3 h-3 ml-1.5" />
                    </Badge>
                  );
                })}
              </div>
              
              {/* Add tags popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-zinc-400 border-zinc-700 hover:bg-zinc-800 hover:text-zinc-200"
                    data-testid="add-tags-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add tags...
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2 bg-zinc-900 border-zinc-800" align="start">
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {tags.length === 0 ? (
                      <p className="text-sm text-zinc-500 p-2">No tags created yet</p>
                    ) : (
                      tags.map((tag) => {
                        const isSelected = (editedAd.tag_ids || []).includes(tag.id);
                        return (
                          <div
                            key={tag.id}
                            className={`
                              flex items-center gap-2 p-2 rounded-lg cursor-pointer
                              ${isSelected ? 'bg-violet-500/20' : 'hover:bg-zinc-800'}
                            `}
                            onClick={() => {
                              if (isSelected) {
                                setEditedAd({
                                  ...editedAd,
                                  tag_ids: (editedAd.tag_ids || []).filter(id => id !== tag.id)
                                });
                              } else {
                                setEditedAd({
                                  ...editedAd,
                                  tag_ids: [...(editedAd.tag_ids || []), tag.id]
                                });
                              }
                            }}
                          >
                            <Checkbox checked={isSelected} className="border-zinc-600" />
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            <span className="text-sm text-zinc-200">{tag.name}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Funnel Stage */}
          <div>
            <Label className="text-zinc-400 text-sm">Funnel Stage</Label>
            <Select
              value={editedAd.funnel_stage || '_none_'}
              onValueChange={(value) => setEditedAd({ ...editedAd, funnel_stage: value === '_none_' ? null : value })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                <SelectValue placeholder="Select stage..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="_none_" className="text-zinc-400">None</SelectItem>
                {FUNNEL_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value} className="text-zinc-200">
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Format */}
          <div>
            <Label className="text-zinc-400 text-sm">Format</Label>
            <Select
              value={editedAd.ad_format || '_none_'}
              onValueChange={(value) => setEditedAd({ ...editedAd, ad_format: value === '_none_' ? null : value })}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                <SelectValue placeholder="Select format..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="_none_" className="text-zinc-400">None</SelectItem>
                {AD_FORMATS.map((format) => (
                  <SelectItem key={format.value} value={format.value} className="text-zinc-200">
                    {format.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hook */}
          <div>
            <Label className="text-zinc-400 text-sm">Hook</Label>
            <Input
              value={editedAd.hook || ''}
              onChange={(e) => setEditedAd({ ...editedAd, hook: e.target.value })}
              placeholder="What's the hook?"
              className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200"
            />
          </div>

          {/* CTA */}
          <div>
            <Label className="text-zinc-400 text-sm">CTA</Label>
            <Input
              value={editedAd.cta || ''}
              onChange={(e) => setEditedAd({ ...editedAd, cta: e.target.value })}
              placeholder="Call to action..."
              className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-zinc-400 text-sm">Notes</Label>
            <Textarea
              data-testid="ad-notes-input"
              value={editedAd.notes || ''}
              onChange={(e) => setEditedAd({ ...editedAd, notes: e.target.value })}
              placeholder="Add notes..."
              className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200 min-h-[100px]"
            />
          </div>

          {/* OCR Text */}
          {ad.ocr_text && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-sm">Extracted Text (OCR)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-zinc-300"
                  onClick={() => copyToClipboard(ad.ocr_text)}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="mt-1.5 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="text-sm text-zinc-400 font-mono whitespace-pre-wrap">
                  {ad.ocr_text}
                </p>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Calendar className="w-3 h-3" />
              Created {new Date(ad.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 pb-8">
          <Button
            data-testid="save-ad-button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-violet-600 hover:bg-violet-500 text-white"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AdDetailPanel;
